/**
 * PoolBoardingPass — la « carte d'embarquement » de l'agent (2026-09-14).
 * =============================================================================
 * JGS : *« comme la SNCF qui pousse ta carte d'embarquement : le siège, où est
 * le véhicule, le code-barres… une nounou pour l'agent, qui n'a plus qu'à aller
 * chercher le véhicule et faire sa mission. »*
 *
 * Une seule lecture serveur (`poolAPI.getBoardingPass`) ; ce composant n'invente
 * rien et ne recalcule rien : les bornes du créneau (prise possible dès,
 * non-présentation à) viennent de la POLITIQUE SERVEUR, le QR est celui de
 * l'étiquette du véhicule, la carte carburant n'a que ses quatre derniers
 * chiffres.
 *
 * Lisible sur un téléphone (une colonne) et imprimable (le bouton, pour les
 * 15 % d'agents sans smartphone : le guichet imprime, l'agent emporte).
 *
 * « Voir sur la carte » réutilise le modal de présence en mode PRÉVU :
 * départ = U.T. d'attache, position = où le véhicule est garé, arrivée =
 * destination.
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Ticket, Car, MapPin, Clock, Flag, QrCode, CreditCard, AlertTriangle, Map as MapIcon, Printer, Building2 } from 'lucide-react';
import poolAPI from '../../../api/pool.js';
import { useFormat } from '../../../i18n/format.js';
import PoolCustodyMapModal from '../PoolCustodyMapModal.jsx';

/** Une ligne de la carte : icône, étiquette, valeur. */
function Row({ icon: Icon, label, children, tone = 'text-gray-400' }) {
  return (
    <div className="flex items-start gap-3">
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${tone}`} />
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
        <div className="text-sm text-gray-900">{children}</div>
      </div>
    </div>
  );
}

export function PoolBoardingPass({ reservationId, driverLabel = null }) {
  const { t } = useTranslation();
  const { fmt } = useFormat();
  const [mapOpen, setMapOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['pool-boarding-pass', String(reservationId ?? '')],
    queryFn: () => poolAPI.getBoardingPass(reservationId),
    enabled: reservationId != null && reservationId !== '',
    refetchInterval: 60_000,
    retry: false,
  });

  const card = data?.ok ? data.data : null;

  // Le modal de présence attend une « garde » : on lui donne la réservation,
  // en mode prévu. Même trajet, mêmes étiquettes.
  const custodyLike = useMemo(() => {
    if (!card) return null;
    return {
      reservation_id: card.reservation.id,
      license_plate: card.vehicle?.license_plate ?? null,
      vehicle_label: [card.vehicle?.make, card.vehicle?.model].filter(Boolean).join(' ') || null,
      driver_label: driverLabel,
      planned_start: card.reservation.planned_start,
      planned_end: card.reservation.planned_end,
      destination_label: card.reservation.destination_label,
      route: card.route,
    };
  }, [card, driverLabel]);

  if (reservationId == null || isError) return null;

  if (isLoading || !card) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="h-3 w-32 animate-pulse rounded bg-gray-100" />
        <div className="mt-3 h-6 w-48 animate-pulse rounded bg-gray-100" />
        <div className="mt-3 h-24 w-full animate-pulse rounded bg-gray-100" />
      </div>
    );
  }

  const { reservation, vehicle, parked_at: parkedAt, route, open_defects: defects, fuel_cards: cards, scan } = card;
  const stamp = (v) => (v ? fmt.dateTime(v) : '—');
  const time = (v) => (v ? fmt.time(v) : '—');
  const hasRoute = !!(route?.origin || route?.destination || route?.vehicle_position);
  const plate = vehicle?.license_plate || null;
  const model = [vehicle?.make, vehicle?.model].filter(Boolean).join(' ') || null;

  return (
    <div className="overflow-hidden rounded-xl border border-blue-200 bg-white shadow-sm print:border-black print:shadow-none" data-testid="pool-boarding-pass">
      {/* Bandeau — le « billet » */}
      <div className="flex items-center justify-between bg-gradient-to-r from-blue-700 to-blue-500 px-4 py-3 text-white print:bg-none print:text-black">
        <div className="flex items-center gap-2">
          <Ticket className="h-5 w-5" />
          <p className="text-sm font-semibold uppercase tracking-wide">{t('poolDriver.boardingPass.title')}</p>
        </div>
        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1 rounded-md bg-white/15 px-2 py-1 text-xs font-medium hover:bg-white/25 print:hidden"
        >
          <Printer className="h-3.5 w-3.5" />
          {t('poolDriver.boardingPass.print')}
        </button>
      </div>

      {/* Le véhicule, en gros — c'est le « siège » */}
      <div className="border-b border-dashed border-gray-300 px-4 py-4">
        <div className="flex items-center gap-3">
          <Car className="h-8 w-8 shrink-0 text-blue-600" />
          <div className="min-w-0">
            {plate ? (
              <p className="font-mono text-2xl font-bold tracking-wider text-gray-900">{plate}</p>
            ) : (
              <p className="text-lg font-semibold text-gray-900">
                {t('poolDriver.boardingPass.byClass', { vehicleClass: reservation.vehicle_class || '—' })}
              </p>
            )}
            <p className="truncate text-sm text-gray-600">
              {model || (plate ? t('poolDriver.boardingPass.unknownModel') : t('poolDriver.boardingPass.assignedAtPickup'))}
              {vehicle?.fuel_type ? ` · ${vehicle.fuel_type}` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 px-4 py-4 md:grid-cols-[1fr_auto]">
        <div className="space-y-4">
          <Row icon={Clock} label={t('poolDriver.boardingPass.departure')} tone="text-emerald-600">
            <p className="font-semibold">{stamp(reservation.planned_start)}</p>
            {reservation.open_from && (
              <p className="text-xs text-gray-500">
                {t('poolDriver.boardingPass.pickupFrom', { time: time(reservation.open_from) })}
                {reservation.no_show_at ? ` · ${t('poolDriver.boardingPass.noShowAt', { time: time(reservation.no_show_at) })}` : ''}
              </p>
            )}
          </Row>

          <Row icon={Clock} label={t('poolDriver.boardingPass.return')}>
            <p className="font-semibold">{stamp(reservation.planned_end)}</p>
          </Row>

          <Row icon={Building2} label={t('poolDriver.boardingPass.whereToFind')} tone="text-blue-600">
            {parkedAt ? (
              <>
                <p className="font-semibold">{parkedAt.site_name || '—'}</p>
                {parkedAt.address && <p className="text-xs text-gray-600">{parkedAt.address}</p>}
              </>
            ) : (
              <p className="text-gray-500">{t('poolDriver.boardingPass.noParkingYet')}</p>
            )}
            {route?.vehicle_position?.age_min != null && (
              <p className="mt-0.5 text-xs text-gray-500">
                {t('poolDriver.boardingPass.lastSeen', { duration: `${route.vehicle_position.age_min} min` })}
              </p>
            )}
            {hasRoute && (
              <button
                type="button"
                onClick={() => setMapOpen(true)}
                className="mt-1.5 inline-flex items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100 print:hidden"
              >
                <MapIcon className="h-3.5 w-3.5" />
                {t('poolPresence.mapButton')}
              </button>
            )}
          </Row>

          <Row icon={Flag} label={t('poolDriver.boardingPass.destination')}>
            <p className="font-semibold">{reservation.destination_label || t('poolDriver.boardingPass.noDestination')}</p>
          </Row>

          <Row icon={AlertTriangle} label={t('poolDriver.boardingPass.knownDefects')} tone={defects?.length ? 'text-amber-500' : 'text-gray-400'}>
            {defects?.length ? (
              <ul className="list-disc space-y-0.5 pl-4 text-sm">
                {defects.map((d) => (
                  <li key={d.id}>
                    {d.zone_label} — {d.damage_label}
                    {d.severity ? <span className="text-xs text-gray-500"> ({d.severity})</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">{t('poolDriver.boardingPass.noDefects')}</p>
            )}
          </Row>

          <Row icon={CreditCard} label={t('poolDriver.boardingPass.fuelCards')}>
            {cards?.length ? (
              <ul className="space-y-0.5">
                {cards.map((c) => (
                  <li key={c.id} className="text-sm">
                    <span className="font-medium">{c.provider || t('poolDriver.boardingPass.fuelCardGeneric')}</span>
                    {c.last4 ? <span className="ml-2 font-mono">•••• {c.last4}</span> : null}
                    <span className="ml-2 text-xs text-gray-500">
                      {t(c.holder === 'driver' ? 'poolDriver.boardingPass.cardYours' : 'poolDriver.boardingPass.cardOnBoard')}
                    </span>
                    {c.pin_required ? <span className="ml-2 text-xs text-gray-500">{t('poolDriver.boardingPass.pinRequired')}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-500">{t('poolDriver.boardingPass.noFuelCard')}</p>
            )}
          </Row>
        </div>

        {/* Le QR de prise — le « code-barres » */}
        <div className="flex flex-col items-center justify-start gap-2 md:w-52">
          {scan?.svg ? (
            <>
              {/* SVG rendu par le serveur (`qrcode`), pas du HTML utilisateur. */}
              <div
                className="w-40 rounded-lg border border-gray-200 bg-white p-2 [&>svg]:h-auto [&>svg]:w-full"
                data-testid="boarding-pass-qr"
                dangerouslySetInnerHTML={{ __html: scan.svg }}
              />
              <p className="flex items-center gap-1 text-center text-xs text-gray-500">
                <QrCode className="h-3.5 w-3.5" />
                {t('poolDriver.boardingPass.scanHint')}
              </p>
            </>
          ) : (
            <p className="text-center text-xs text-gray-500">{t('poolDriver.boardingPass.noQr')}</p>
          )}
        </div>
      </div>

      <p className="border-t border-gray-100 px-4 py-2 text-xs text-gray-500">
        {t('poolDriver.boardingPass.contact')}
      </p>

      {mapOpen && custodyLike && (
        <PoolCustodyMapModal custody={custodyLike} upcoming kind="driver" onClose={() => setMapOpen(false)} />
      )}
    </div>
  );
}

export default PoolBoardingPass;
